import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Tabs,
  Tab,
  Stack,
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import { 
  DocumentArrowDownIcon, 
  DocumentArrowUpIcon, 
  CheckCircleIcon,
  PencilSquareIcon,
  XMarkIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import Swal from 'sweetalert2';
import { pdf } from '@react-pdf/renderer';
import DeliveryActPdf from '../proyectos/components/DeliveryActPdf.jsx';
import { decrypt } from '../../utils/crypto.js';
import { BASE_URL } from '../../App';

// --- Reusable Signature Canvas Component ---
function SignatureCanvasPad({ onSave, title, initialDataUrl }) {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const [history, setHistory] = useState([]);
  const [hasDrawn, setHasDrawn] = useState(Boolean(initialDataUrl));

  const initContext = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    return ctx;
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches && e.touches.length > 0 ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches && e.touches.length > 0 ? e.touches[0].clientY : e.clientY;
    const scaleX = canvas.width / (rect.width || 1);
    const scaleY = canvas.height / (rect.height || 1);
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = initContext();
    if (!ctx) return;

    try {
      const currentImgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      setHistory((prev) => [...prev, currentImgData]);
    } catch (err) {}

    isDrawingRef.current = true;
    setHasDrawn(true);
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    ctx.lineTo(pos.x + 0.1, pos.y + 0.1);
    ctx.stroke();

    if (e.cancelable && e.touches) {
      e.preventDefault();
    }
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    if (e.cancelable && e.touches) {
      e.preventDefault();
    }
  };

  const stopDrawing = () => {
    if (isDrawingRef.current) {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.closePath();
      }
      isDrawingRef.current = false;
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    initContext();

    if (initialDataUrl) {
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = initialDataUrl;
    }

    const handleTouchStart = (e) => startDrawing(e);
    const handleTouchMove = (e) => draw(e);
    const handleTouchEnd = () => stopDrawing();

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHistory([]);
    setHasDrawn(false);
  };

  const undo = () => {
    if (history.length === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const lastState = history[history.length - 1];
    ctx.putImageData(lastState, 0, 0);
    setHistory((prev) => prev.slice(0, -1));
    if (history.length <= 1) {
      setHasDrawn(false);
    }
  };

  const handleConfirm = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn) {
      Swal.fire('Atención', 'Por favor realiza el trazo de la firma.', 'warning');
      return;
    }
    onSave(canvas.toDataURL());
  };

  return (
    <Box sx={{ mt: 1 }}>
      {title && (
        <Typography variant="subtitle2" fontWeight="700" gutterBottom sx={{ color: '#334155' }}>
          {title}
        </Typography>
      )}
      <Box sx={{ border: '2px dashed #94a3b8', borderRadius: 2.5, overflow: 'hidden', bgcolor: '#fff', touchAction: 'none' }}>
        <canvas
          ref={canvasRef}
          width={450}
          height={180}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          style={{
            display: 'block',
            cursor: 'crosshair',
            width: '100%',
            height: '180px',
            touchAction: 'none'
          }}
        />
      </Box>
      <Box display="flex" justifyContent="space-between" mt={1.5} gap={1}>
        <Box display="flex" gap={1}>
          <Button size="small" variant="outlined" color="secondary" onClick={undo} disabled={history.length === 0} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Deshacer
          </Button>
          <Button size="small" variant="outlined" color="error" onClick={clear} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Borrar
          </Button>
        </Box>
        <Button size="small" variant="contained" color="primary" onClick={handleConfirm} sx={{ textTransform: 'none', borderRadius: 2 }}>
          Confirmar Trazo
        </Button>
      </Box>
    </Box>
  );
}

export default function ProyectosCerrados() {
  const [allClosedProjects, setAllClosedProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: Pendientes de Firma, 1: Firmadas
  const [downloading, setDownloading] = useState(null);
  const [uploading, setUploading] = useState(null);

  // Digital Signature Modal State
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [signingProject, setSigningProject] = useState(null);
  const [signatureStep, setSignatureStep] = useState(0); // 0: Tech, 1: Client
  const [savedSignatures, setSavedSignatures] = useState([]);
  const [isDrawingNewTech, setIsDrawingNewTech] = useState(false);
  const [techSignatureData, setTechSignatureData] = useState(null);
  const [clientSignatureData, setClientSignatureData] = useState(null);
  const [clientSignerName, setClientSignerName] = useState('');
  const [submittingSignature, setSubmittingSignature] = useState(false);

  const company = sessionStorage.getItem('company');

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/getProjects/${company}?tipo=proyecto`);
      const all = response.data.data || [];
      const closed = all.filter(p => p.state === 'Finalizado');
      setAllClosedProjects(closed);
    } catch (error) {
      console.error('Error fetching closed projects:', error);
      Swal.fire('Error', 'No se pudieron cargar los proyectos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMySignatures = async () => {
    try {
      const res = await axios.get('/getMySignatures');
      const sigs = res.data.data || [];
      setSavedSignatures(sigs);
      if (sigs.length === 0) {
        setIsDrawingNewTech(true);
      }
    } catch (error) {
      console.error('Error fetching technician signature:', error);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchMySignatures();
  }, [company]);

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('data:') || path.startsWith('blob:')) return path;
    const base = BASE_URL.endsWith('/') ? BASE_URL : BASE_URL + '/';
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    const token = sessionStorage.getItem('Token') || '';
    return `${base}${cleanPath}?token=${encodeURIComponent(token)}`;
  };

  const fetchImageAsBase64 = async (path) => {
    if (!path) return null;
    if (path.startsWith('data:') || path.startsWith('blob:')) return path;
    try {
      const url = getImageUrl(path);
      const response = await axios.get(url, { responseType: 'blob' });
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(response.data);
      });
    } catch (error) {
      console.error('Error fetching image as base64:', error);
      return null;
    }
  };

  // Projects pending signature (disappear from technician once signed)
  const pendingProjects = allClosedProjects.filter(p => !p.signed_act);
  // Projects with signed act
  const signedProjects = allClosedProjects.filter(p => Boolean(p.signed_act));

  const displayedProjects = activeTab === 0 ? pendingProjects : signedProjects;

  const handleDownloadActa = async (project) => {
    try {
      setDownloading(project.id);
      const res = await axios.get(`/getOneProject/${project.id}`);
      const fullProjectData = res.data.data?.[0] || res.data?.[0] || res.data;
      const user = decrypt(sessionStorage.getItem('name'));

      const blob = await pdf(<DeliveryActPdf project={fullProjectData} user={user} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `acta_entrega_proyecto_${project.id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'No se pudo generar el acta', 'error');
    } finally {
      setDownloading(null);
    }
  };

  const handleFileUpload = async (event, project) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      Swal.fire('Formato Inválido', 'El acta debe ser un archivo PDF.', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('signed_act', file);

    setUploading(project.id);
    try {
      await axios.post(`/uploadSignedAct/${project.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      Swal.fire({
        title: '¡Acta Subida!',
        text: 'El acta firmada se ha cargado correctamente y el proyecto ha concluido.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
      fetchProjects();
    } catch (error) {
      console.error('Error uploading signed act:', error);
      Swal.fire('Error', 'Hubo un problema al subir el archivo.', 'error');
    } finally {
      setUploading(null);
      event.target.value = null;
    }
  };

  // --- Digital Signature Flow ---
  const handleOpenSignModal = (project) => {
    setSigningProject(project);
    setSignatureStep(0);
    setTechSignatureData(null);
    setClientSignatureData(null);
    setClientSignerName(project.customerName || '');
    setIsDrawingNewTech(savedSignatures.length === 0);
    setSignModalOpen(true);
  };

  const handleCloseSignModal = () => {
    if (submittingSignature) return;
    setSignModalOpen(false);
    setSigningProject(null);
  };

  const handleUseSavedTechSignature = async () => {
    if (savedSignatures.length === 0) return;
    try {
      const base64 = await fetchImageAsBase64(savedSignatures[0].signature);
      if (base64) {
        setTechSignatureData(base64);
        setSignatureStep(1); // Proceed to client signature
      } else {
        Swal.fire('Error', 'No se pudo cargar la firma guardada', 'error');
      }
    } catch (e) {
      Swal.fire('Error', 'Error al procesar la firma', 'error');
    }
  };

  const handleConfirmDrawnTechSignature = (dataUrl) => {
    setTechSignatureData(dataUrl);
    setSignatureStep(1); // Proceed to client signature
  };

  const handleConfirmClientSignature = (dataUrl) => {
    setClientSignatureData(dataUrl);
  };

  const handleSubmitDigitalAct = async () => {
    if (!techSignatureData) {
      Swal.fire('Atención', 'Falta la firma del técnico responsable.', 'warning');
      setSignatureStep(0);
      return;
    }
    if (!clientSignatureData) {
      Swal.fire('Atención', 'Por favor confirme el trazo de la firma del cliente.', 'warning');
      return;
    }
    if (!clientSignerName.trim()) {
      Swal.fire('Atención', 'Por favor ingrese el nombre de quien recibe por el cliente.', 'warning');
      return;
    }

    try {
      setSubmittingSignature(true);
      Swal.fire({
        title: 'Generando Acta Firmada...',
        text: 'Insertando firmas digitales y preparando documento PDF.',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      // Fetch complete project details for the PDF
      const res = await axios.get(`/getOneProject/${signingProject.id}`);
      const fullProjectData = res.data.data?.[0] || res.data?.[0] || res.data;
      const user = decrypt(sessionStorage.getItem('name')) || 'Técnico Responsable';

      // Generate Delivery Act PDF with both digital signatures
      const actBlob = await pdf(
        <DeliveryActPdf
          project={fullProjectData}
          user={user}
          technicianSignature={techSignatureData}
          customerSignature={clientSignatureData}
          customerSignerName={clientSignerName.trim()}
        />
      ).toBlob();

      // Wrap in FormData and upload
      const formData = new FormData();
      formData.append(
        'signed_act',
        actBlob,
        `acta_entrega_firmada_${signingProject.id}_${Date.now()}.pdf`
      );

      await axios.post(`/uploadSignedAct/${signingProject.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      Swal.close();
      await Swal.fire({
        title: '¡Acta Firmada y Guardada!',
        text: 'El acta digital fue firmada exitosamente. El proyecto ha concluido y desaparece de la lista de pendientes.',
        icon: 'success'
      });

      setSignModalOpen(false);
      setSigningProject(null);
      fetchProjects(); // Project immediately disappears from pending
    } catch (error) {
      console.error('Error generating and uploading signed act:', error);
      Swal.fire('Error', 'Hubo un error al generar o subir el acta firmada.', 'error');
    } finally {
      setSubmittingSignature(false);
    }
  };

  const handleDownloadSignedActFile = (project) => {
    if (!project.signed_act) return;
    const url = getImageUrl(`uploads/documents/${project.signed_act}`);
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1300, mx: 'auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight="800" sx={{ color: '#0f172a' }}>
            Actas de Entrega de Proyectos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestión y firma digital de actas para proyectos finalizados.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowPathIcon className="w-4 h-4" />}
          onClick={fetchProjects}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Actualizar
        </Button>
      </Box>

      {/* Tabs: Pendientes vs Firmadas */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>Pendientes de Firma</span>
                <Chip
                  label={pendingProjects.length}
                  size="small"
                  color={pendingProjects.length > 0 ? "warning" : "default"}
                  sx={{ fontWeight: '700', height: 20 }}
                />
              </Box>
            }
            sx={{ textTransform: 'none', fontWeight: '700' }}
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>Historial Firmadas</span>
                <Chip
                  label={signedProjects.length}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: '700', height: 20 }}
                />
              </Box>
            }
            sx={{ textTransform: 'none', fontWeight: '700' }}
          />
        </Tabs>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={6}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                <TableRow>
                  <TableCell><b>ID Proyecto</b></TableCell>
                  <TableCell><b>Cliente</b></TableCell>
                  <TableCell><b>Sistema Motriz</b></TableCell>
                  <TableCell><b>Estado Acta</b></TableCell>
                  <TableCell align="right"><b>Acciones</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CheckCircleIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <Typography variant="subtitle1" fontWeight="600" color="text.secondary">
                        {activeTab === 0
                          ? 'No hay proyectos pendientes de firma. ¡Todo al día!'
                          : 'Aún no hay actas firmadas en el historial.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedProjects.map((project) => (
                    <TableRow key={project.id} hover>
                      <TableCell>
                        <Chip
                          label={`#${project.id}`}
                          size="small"
                          sx={{ fontWeight: '700', bgcolor: '#f1f5f9', color: '#1e293b' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: '600', color: '#1e293b' }}>
                        {project.customerName || 'S/N'}
                      </TableCell>
                      <TableCell>{project.elevatorTypeName || 'S/N'}</TableCell>
                      <TableCell>
                        {project.signed_act ? (
                          <Chip
                            icon={<CheckCircleIcon className="w-4 h-4 text-emerald-600" />}
                            label="Acta Firmada"
                            color="success"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: '600' }}
                          />
                        ) : (
                          <Chip
                            label="Pendiente de Firma"
                            color="warning"
                            size="small"
                            sx={{ fontWeight: '600' }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
                          {!project.signed_act ? (
                            <>
                              {/* 1. Digital Signature Button */}
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleOpenSignModal(project)}
                                startIcon={<PencilSquareIcon className="w-4 h-4" />}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: 2,
                                  fontWeight: '700',
                                  bgcolor: '#2563eb',
                                  boxShadow: 'none',
                                  '&:hover': { bgcolor: '#1d4ed8' }
                                }}
                              >
                                Firmar Digitalmente
                              </Button>

                              {/* 2. Download Draft Acta */}
                              <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                onClick={() => handleDownloadActa(project)}
                                disabled={downloading === project.id}
                                startIcon={<DocumentArrowDownIcon className="w-4 h-4" />}
                                sx={{ textTransform: 'none', borderRadius: 2, color: '#475569', borderColor: '#cbd5e1' }}
                              >
                                {downloading === project.id ? 'Descargando...' : 'Descargar Borrador'}
                              </Button>

                              {/* 3. Manual PDF Upload */}
                              <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                disabled={uploading === project.id}
                                component="label"
                                startIcon={<DocumentArrowUpIcon className="w-4 h-4" />}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                              >
                                {uploading === project.id ? 'Subiendo...' : 'Subir Firmada'}
                                <input
                                  type="file"
                                  hidden
                                  accept="application/pdf"
                                  onChange={(e) => handleFileUpload(e, project)}
                                />
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={() => handleDownloadSignedActFile(project)}
                              startIcon={<DocumentArrowDownIcon className="w-4 h-4" />}
                              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: '600' }}
                            >
                              Ver / Descargar Acta Firmada
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* --- Digital Signature Modal --- */}
      <Dialog
        open={signModalOpen}
        onClose={handleCloseSignModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight="800" sx={{ color: '#0f172a' }}>
              Firma Digital de Acta de Entrega
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Proyecto #{signingProject?.id} - {signingProject?.customerName}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseSignModal} disabled={submittingSignature} size="small">
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Stepper activeStep={signatureStep} alternativeLabel sx={{ mb: 3 }}>
            <Step completed={Boolean(techSignatureData)}>
              <StepLabel>Firma del Técnico</StepLabel>
            </Step>
            <Step completed={Boolean(clientSignatureData)}>
              <StepLabel>Firma del Cliente</StepLabel>
            </Step>
          </Stepper>

          {/* STEP 0: Technician Signature */}
          {signatureStep === 0 && (
            <Box>
              {!isDrawingNewTech && savedSignatures.length > 0 ? (
                <Stack spacing={2.5}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Se detectó tu firma registrada como técnico. Puedes usarla directamente o dibujar una nueva para este acta.
                  </Alert>

                  <Paper
                    sx={{
                      p: 2,
                      border: '2px solid #e2e8f0',
                      borderRadius: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      bgcolor: '#f8fafc'
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 300,
                        height: 120,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#fff',
                        borderRadius: 2,
                        p: 1,
                        border: '1px solid #e2e8f0',
                        mb: 1.5
                      }}
                    >
                      <img
                        src={getImageUrl(savedSignatures[0].signature)}
                        alt="Firma Guardada"
                        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                    <Typography variant="subtitle2" fontWeight="700" color="text.primary">
                      {savedSignatures[0].name || 'Firma Guardada de Técnico'}
                    </Typography>
                  </Paper>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleUseSavedTechSignature}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: '800',
                      py: 1.2,
                      bgcolor: '#2563eb'
                    }}
                  >
                    Usar Mi Firma Guardada y Continuar
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setIsDrawingNewTech(true)}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: '600',
                      color: '#475569',
                      borderColor: '#cbd5e1'
                    }}
                  >
                    Dibujar Nueva Firma de Técnico
                  </Button>
                </Stack>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Dibuja tu firma en el recuadro a continuación:
                  </Typography>

                  <SignatureCanvasPad
                    title="Firma del Técnico Responsable (DIMEI)"
                    initialDataUrl={techSignatureData}
                    onSave={handleConfirmDrawnTechSignature}
                  />

                  {savedSignatures.length > 0 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setIsDrawingNewTech(false)}
                      sx={{ mt: 2, textTransform: 'none' }}
                    >
                      ← Volver a usar mi firma guardada
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* STEP 1: Client Signature */}
          {signatureStep === 1 && (
            <Box>
              <TextField
                fullWidth
                label="Nombre de quien recibe (Cliente / Representante)"
                variant="outlined"
                value={clientSignerName}
                onChange={(e) => setClientSignerName(e.target.value)}
                placeholder="Ej. Juan Pérez - Administrador"
                sx={{ mb: 2 }}
                size="small"
                required
              />

              <SignatureCanvasPad
                title="Firma de Recibido a Conformidad (Cliente)"
                initialDataUrl={clientSignatureData}
                onSave={handleConfirmClientSignature}
              />

              {clientSignatureData && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                  Firma del cliente confirmada. Presiona <b>"Finalizar y Guardar Acta"</b> para estampar las firmas en el PDF.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          {signatureStep === 1 ? (
            <Button
              variant="outlined"
              onClick={() => setSignatureStep(0)}
              disabled={submittingSignature}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              ← Volver a Firma Técnico
            </Button>
          ) : (
            <Button
              variant="text"
              onClick={handleCloseSignModal}
              disabled={submittingSignature}
              sx={{ textTransform: 'none' }}
            >
              Cancelar
            </Button>
          )}

          {signatureStep === 1 && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmitDigitalAct}
              disabled={submittingSignature || !clientSignatureData}
              sx={{
                textTransform: 'none',
                fontWeight: '800',
                borderRadius: 2,
                px: 3,
                bgcolor: '#16a34a',
                '&:hover': { bgcolor: '#15803d' }
              }}
            >
              {submittingSignature ? 'Generando PDF...' : 'Finalizar y Guardar Acta'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
